// item_ses builder — port of OrderCreateViewModel.toParseJson (OCV 696-1099), cross-checked against
// the server keys Process_order/saveOrderPos actually reads. Emits one object per real cart line
// (splits/synthetic lines excluded). ALL values are Strings (the server form-decodes strings).
// Per-line product identity key is `tag_id` (NOT product_id) — the server reads $item->tag_id.
//
// Run ONLY after the pricing pipeline (recalculate) — it reads engine-set jsonNewPrice / discount /
// tax fields. Financially-material keys are faithful; note/op/v metadata is best-effort for v1.

import { roundHalfUp, truncate2, toNum } from "./money";

const S = (v) => (v == null ? "" : String(v));
const money = (v) => roundHalfUp(v).toFixed(2);
// Android getAltriaDiscountValue: round half-up, floor at 0 (0 → JSON number 0).
const altriaVal = (x) => roundHalfUp(Math.max(toNum(x), 0));
// Android normalizedAltriaFunding: a non-blank string, else "0".
const normAltria = (v) => (v != null && String(v).trim() !== "" ? String(v) : "0");

function catalogOf(line) {
  const m = line.model || {};
  if (line.catalogUnitPrice != null) return toNum(line.catalogUnitPrice);
  return toNum(line.isVariant ? m.vPrice : m.price);
}

export function buildItemSession(cart, taxResult = {}, ctx = {}) {
  const perLineDefaultTax = taxResult.perLineDefaultTax || new Map();
  const perLineOtherTax = taxResult.perLineOtherTax || new Map();
  const out = [];

  for (const line of cart) {
    if (line.isSplitPaymentDiscount || line.isSplitPayment || line.isSplitRefund) continue;

    const m = line.model || {};
    const count = toNum(line.orderCount);
    const isVar = !!line.isVariant;
    const useVar = isVar && !!m.id; // custom items (no model.id) use non-variant food/tobacco flags
    const catalog = catalogOf(line);
    const inventoryUnit = toNum(line.orderUnitNewPrice) > 0 ? toNum(line.orderUnitNewPrice) : toNum(line.orderUnitPrice);

    // item-level discount. discount_amt is per-unit (matches Android): % => truncate2(unit*pct/100), $ => flat.
    // With an item coupon (QV-4738), `flat` = orderDiscount = manual + coupon; discount_amt goes out
    // MANUAL-only below and the coupon ships separately in item_coupon_json.
    const pct = toNum(line.orderDiscountPercent);
    const flat = toNum(line.orderDiscount);
    const itemDiscPerUnit = pct > 0 ? truncate2((inventoryUnit * pct) / 100) : flat;
    const hasItemCoupon = line.couponDiscountName != null && String(line.couponDiscountName) !== "";
    const couponPerUnit = hasItemCoupon ? roundHalfUp(toNum(line.couponDiscountFinalAmt)) : 0;
    // price — Android toParseJson L1036-1077: with a discount → jsonNewPrice (line net) + PER-UNIT item
    // discount re-added; else a LOTTERY/scratcher line → orderUnitPrice; else jsonNewPrice. Clamp at 0.
    const isLotteryLine = line.isLotteryTicket === "1" || line.isLotteryPayout || line.isScratcherPayout;
    let price =
      pct > 0 || flat > 0
        ? toNum(line.jsonNewPrice) + itemDiscPerUnit
        : isLotteryLine
        ? toNum(line.orderUnitPrice)
        : toNum(line.jsonNewPrice);
    if (price < 0) price = 0;

    // taxes
    const dTax = perLineDefaultTax.get(line.orderId);
    const oTaxList = perLineOtherTax.get(line.orderId) || [];
    const otherTotal = oTaxList.reduce((s, t) => s + toNum(t.price), 0);
    const defaultTaxTotalPerUnit = dTax ? toNum(dTax.price) / (count || 1) : 0;
    const otherTaxTotalPerUnit = otherTotal / (count || 1);

    // promotions — Android: bogo_discount only meaningful when isBoGo; mix_match_amt XOR adjust_price.
    const bogoDiscount = line.isBoGoProduct ? (catalog - toNum(line.orderUnitPrice)) * count : 0;
    // QV Altria coupon node (Android ee2ae0d512, 2026-08-20): the Altria-funded switch moved OFF the
    // order-level coupon (whose saves now always send altria_funded_discount=0) onto SPECIFIC item
    // coupons, so `altria_discount_desc.coupon` reports the ITEM coupon — code + its LINE TOTAL
    // (per-unit final × qty, like bogo/mix_match) + the flag captured on the line at apply time.
    const itemCouponLineTotal = hasItemCoupon ? roundHalfUp(toNum(line.couponDiscountFinalAmt) * count) : 0;

    const isLottery = line.isLotteryTicket === "1" ? "1" : "0";
    const couponCodeAmt = isLottery === "1" || line.isLotteryPayout || line.isScratcherPayout ? 0 : toNum(line.couponDiscountAmt);

    // The LINE's variant combo comes from orderName ("<title>-r/s/2"), NOT model.vVariant. In the web,
    // each cart line's model.vVariant is the product's FULL variant matrix (all combos), whereas Android's
    // per-line model holds just that line's variant. Deriving from orderName yields the line-specific
    // values Android emits (e.g. Color-r~Size-s~Count-2), not the whole matrix (Color-b, g, r~...).
    let vParts = ["", "", ""];
    let vCount = 0;
    if (isVar) {
      const title = S(m.title);
      const nm = S(line.orderName);
      const variantName = title && nm.startsWith(`${title}-`) ? nm.slice(title.length + 1) : nm;
      const parts = variantName.split("/").map((x) => x.trim());
      vCount = parts.length;
      vParts = [parts[0] || "", parts[1] || "", parts[2] || ""];
    }
    const v1 = vParts[0], v2 = vParts[1], v3 = vParts[2];

    // note — Android sbNote (L806-831): "Name-<title>" + "~<optionsN>-<lineVariantValueN>" for each
    // non-empty optionsN that has a variant token.
    let note = `Name-${S(m.title)}`;
    if (m.options1 && vCount >= 1) note += `~${S(m.options1)}-${vParts[0]}`;
    if (m.options2 && vCount >= 2) note += `~${S(m.options2)}-${vParts[1]}`;
    if (m.options3 && vCount >= 3) note += `~${S(m.options3)}-${vParts[2]}`;

    // other-tax per-line — Android emits JSON OBJECT strings, not CSV:
    //   other_tax_desc         = {name: (linePrice/count) half-up}      (getOtherTaxDesc)
    //   other_taxes_rate_desc  = {name: percent}  for price>0            (getOtherTaxRateTotal)
    const otherDescObj = {};
    const otherRateObj = {};
    for (const t of oTaxList) {
      const nm = t.displayName || t.title;
      otherDescObj[nm] = roundHalfUp(toNum(t.price) / (count || 1));
      if (toNum(t.price) > 0) otherRateObj[nm] = toNum(t.percent);
    }

    const item = {
      use_point: line.canUsePoint ? "1" : "0",
      earn_point: line.canEarnPoint ? "1" : "0",
      name: S(m.title), // Android: product title ONLY (fullname carries the display/order name)
      note,
      // ⚠️ `m.vupc` is the COMMA-JOINED list of every variant's code — sending it saved a variant line
      // under "code1,code2,code3", which no lookup can match (and which mKonnekt/Altria reporting
      // would reject). The chosen variant's own code is stamped on the line at add time.
      upc: S(isVar ? line.variantUpc || "" : m.upc),
      cost_price: money(isVar ? toNum(m.vCostPrice) : toNum(m.costperItem)),
      tag_id: S(line.orderProductId), // product id (server reads $item->tag_id as product_id)
      product_id: S(line.orderProductId),
      inventory_price: money(catalog), // catalog/list unit price (catalogUnitPrice) — NOT m.vPrice, which can carry a stale 5.01
      is_lottery: isLottery,
      sp_quty: S(count),
      position_id: "",
      cat: "",
      op1: `${S(m.options1)}-${v1}`,
      op2: `${S(m.options2)}-${v2}`,
      op3: `${S(m.options3)}-${v3}`,
      option1: S(m.options1),
      option2: S(m.options2),
      option3: S(m.options3),
      v1,
      v2,
      v3,
      fullname: S(line.orderName), // the display/order name
      // Android 7494508ccf/abfa61e846: a variant with its own image sends that (bare filename), else
      // the product's first image.
      img: S(isVar && line.variantMedia ? String(line.variantMedia).split(",")[0] : m.media ? String(m.media).split(",")[0] : ""),
      category_id: S((S(m.cotegory).split(",")[0] || "").trim()),
      category_name: S(m.categoryName || "Custom Category"),
      default_tax_rate: dTax ? S(dTax.percent) : "0",
      default_tax_total: money(defaultTaxTotalPerUnit),
      other_tax_total: money(otherTaxTotalPerUnit),
      other_taxes_rate_desc: Object.keys(otherRateObj).length ? JSON.stringify(otherRateObj) : "",
      other_tax_desc: oTaxList.length ? JSON.stringify(otherDescObj) : "",
      bogo_id: toNum(line.bogoIdentifier) > 0 ? S(line.bogoDealId) : "",
      custom_discount_amt: money(line.customDiscountAmt),
      coupon_code_amt: money(couponCodeAmt),
      // altria_discount_desc — Android buildAltriaDiscountDesc (L1144-1207): per coupon/bogo/mix_match,
      // { code: dealName when its discount>0 else "", discount: getAltriaDiscountValue, altria_funded_discount }.
      // + promotion_code (web ahead of Android): the deal's Altria LFP promotion code when it discounted
      // this line, else "". ALWAYS present, like the rest of the node. The AGDC scan report puts an
      // Altria-funded deal that carries a code in fields 29/30, and a code stated here — the value at
      // sale time — beats its report-time lookup by deal name, which only sees today's value.
      altria_discount_desc: JSON.stringify({
        coupon: {
          code: itemCouponLineTotal > 0 ? S(line.couponDiscountName) : "",
          discount: altriaVal(itemCouponLineTotal),
          altria_funded_discount: itemCouponLineTotal > 0 ? normAltria(line.couponAltriaFunded) : "0",
          promotion_code: itemCouponLineTotal > 0 ? S(line.couponPromotionCode) : "",
        },
        bogo: {
          code: bogoDiscount > 0 ? S(line.bogoDealName) : "",
          discount: altriaVal(bogoDiscount),
          altria_funded_discount: bogoDiscount > 0 ? normAltria(line.bogoAltriaFunded) : "0",
          promotion_code: bogoDiscount > 0 ? S(line.bogoPromotionCode) : "",
        },
        mix_match: (() => {
          const md = line.isBulkPriceApplied ? (catalog - toNum(line.orderUnitNewPrice)) * count : 0;
          return {
            code: md > 0 ? S(m.bulkDealName) : "",
            discount: altriaVal(md),
            altria_funded_discount: md > 0 ? normAltria(m.bulkAltriaFundedDiscount) : "0",
            // Android 77992678da: read at SAVE time from the current deal (refreshed list), so an edit
            // made while the line sits in the cart is not saved stale; falls back to the add-time stamp.
            promotion_code:
              md > 0
                ? S(ctx.mixMatchPromoById && m.bpId != null && ctx.mixMatchPromoById.has(String(m.bpId)) ? ctx.mixMatchPromoById.get(String(m.bpId)) : m.bulkPromotionCode)
                : "",
          };
        })(),
        // QV-7124 Altria manufacturer-discount node. ALWAYS present and zeroed on a non-dezi line, so
        // the backend parses ONE shape — sibling-consistent with coupon/bogo/mix_match above.
        // ⚠️ `discount` is a LINE TOTAL (per-unit × qty); the per-unit value lives in discount_amt.
        // Anything reading this node back must NOT multiply by qty again.
        // Σ breakdown === discount exactly, by construction (see engine/dezi applyDeziItemDiscounts).
        mkonnekt: {
          discount: roundHalfUp(toNum(line.deziItemDiscountAmt) * count),
          quantity: count,
          breakdown: (line.deziBreakdown || []).map((b) => ({
            id: S(b.id),
            itemUPC: S(b.itemUPC),
            name: S(b.name),
            discountType: S(b.discountType),
            amount: roundHalfUp(b.amount),
            opCompanyCode: S(b.opCompanyCode),
            couponCode: S(b.couponCode),
          })),
        },
      }),
      // QV-4738: with an item coupon, discount_amt stays MANUAL-only (orderDiscount − coupon) and
      // discount_rate is forced "0.00" (Android toParseJson) — the coupon is sent in item_coupon_json.
      discount_rate: hasItemCoupon ? "0.00" : pct > 0 ? S(pct) : "0.00",
      discount_amt: hasItemCoupon
        ? money(Math.max(flat - couponPerUnit, 0))
        : pct > 0
        ? money(itemDiscPerUnit)
        : flat > 0
        ? money(flat)
        : "0.00",
      // item_coupon_json is ALWAYS present (empty shape when no coupon) — a nested JSON OBJECT with
      // NUMBER values, exactly like Android's JSONObject. discount_amount is the per-unit coupon $.
      item_coupon_json: hasItemCoupon
        ? {
            coupon_code: S(line.couponDiscountName),
            discount_type: S(line.couponDiscountType),
            discount_value: toNum(line.couponDiscountValue),
            discount_amount: couponPerUnit,
          }
        : { coupon_code: "", discount_type: "", discount_value: 0, discount_amount: 0 },
      // flat key for the backend (Vikas) — same per-unit value as item_coupon_json.discount_amount
      item_coupon_discount_amount: hasItemCoupon ? couponPerUnit : 0,
      price: money(price),
      admin_id: S(ctx.adminId),
      variant_id: isVar ? S(line.variantId) : "", // Android: model.vVariantId when variant, else ""
      brand_id: S(m.brandId),
      brand_name: S(m.brand),
      is_bulk_price: line.isBulkPriceApplied ? "1" : "0",
      bulk_price_id: S(m.bpId),
      loyalty_discount_amt: money(line.jsonLoyaltyAmt),
      uom: S(m.uom),
      manufacturer: S(m.manufacturer),
      consumer_unit: S(m.consumerUnit),
      // Custom line items (model.id empty) use the NON-variant food/tobacco flags even if flagged variant (Android L1120-1132).
      is_foodstamp: ((useVar ? m.vFoodStampable : m.foodStampable) === "1" || (useVar ? m.vFoodStampable : m.foodStampable) === 1) ? "1" : "0",
      is_tobacco: ((useVar ? m.vIsTobacco : m.isTobacco) === "1" || (useVar ? m.vIsTobacco : m.isTobacco) === 1) ? "1" : "0",
    };

    // bogo_discount — Android only emits this key on actual BOGO rows (toParseJson L956-963); omit otherwise.
    if (line.isBoGoProduct) item.bogo_discount = money(bogoDiscount);

    // mix_match_amt XOR adjust_price — Android (toParseJson L986-998) emits exactly ONE:
    //   bulk approved → mix_match_amt = (newPrice - unitPrice)*count
    //   else override → adjust_price = (unitPrice - newPrice)*count ; else adjust_price "0.00"
    if (line.isBulkPriceApplied && toNum(line.orderUnitNewPrice) !== 0) {
      item.mix_match_amt = String((toNum(line.orderUnitNewPrice) - toNum(line.orderUnitPrice)) * count);
    } else if (toNum(line.orderUnitNewPrice) > 0) {
      item.adjust_price = String((toNum(line.orderUnitPrice) - toNum(line.orderUnitNewPrice)) * count);
    } else {
      item.adjust_price = "0.00";
    }

    out.push(item);
  }
  return out;
}
